import { FileMinusIcon, FileTextIcon, SparklesIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { DocumentoLeidoPorLaIa, DocumentoSinUsar } from "@/lib/rellenoConIa";

/**
 * "Rellenar con IA": el botón de la pestaña y el aviso de lo que trajo.
 *
 * PRESENTACIONAL, como {@see ZonaDeImagen} y {@see ImportadorDeTasacion}: no
 * sabe de la API ni del borrador. Quien llama es la pestaña, que es la que
 * puede mezclar la propuesta con lo que ya hay cargado.
 *
 * SON DOS PIEZAS Y NO UNA porque van en dos lugares de la pantalla: el botón
 * arriba a la derecha, junto al título, y el aviso debajo, donde se lee después
 * de apretarlo. Un solo componente obligaría a pasar el layout de la pestaña
 * por props.
 *
 * VIVEN EN UN ARCHIVO APARTE Y NO DENTRO DE `PestanaDeAguas` porque la
 * funcionalidad va a entrar en las otras pestañas técnicas: lo único propio de
 * cada una es qué se propone, no cómo se pide ni cómo se avisa.
 */

/**
 * Lo que hay que contarle a la persona después de un relleno.
 *
 * LOS DOCUMENTOS NO SON UN EXTRA. Una propuesta vacía y un expediente al que le
 * falta el certificado de derechos de agua producen exactamente la misma
 * pantalla, y se arreglan al revés: uno es que el predio no tiene agua, el otro
 * es pedirle el papel al cliente. Esta lista es lo único que los separa.
 */
export interface AvisoDelRelleno {
  /** Qué se agregó, ya en palabras ("Se agregaron 2 fuentes de agua y 1 obra."). */
  resumen: string;
  documentos: DocumentoLeidoPorLaIa[];
  /** Documentos que sirven para la pestaña y todavía no se pudieron leer. */
  pendientes: number;
  /**
   * Documentos que se PODÍAN leer y no se leyeron: para cada dato que podían
   * traer ya había uno de mayor prioridad.
   *
   * ES EL TERCER CASO Y TIENE OTRA SALIDA. Con un pendiente hay que esperar al
   * transcriptor; con un documento que falta hay que pedírselo al cliente; acá
   * el papel está y se puede leer, y lo que hay que decidir es cuál de los dos
   * se quiere. Por eso va en su propio bloque y no sumado a `pendientes`.
   */
  sinUsar: DocumentoSinUsar[];
}

/**
 * Qué hacer ahora, en las pestañas cuya propuesta queda en el BORRADOR.
 *
 * Son mayoría —aguas, suelos— y por eso es el defecto. NO NOMBRA NINGÚN DATO DE
 * NINGUNA PESTAÑA (decía "sobre todo los caudales" cuando solo existía aguas):
 * el componente lo comparten todas, y una frase por pestaña sería el mismo aviso
 * escrito N veces, con la que se olvide de actualizar mandando a revisar un dato
 * que esa pantalla no tiene.
 *
 * La excepción es "Maquinarias y equipos", que pasa la suya por `queHacer`
 * porque ahí agregar una fila SÍ toca el servidor.
 */
const QUE_HACER_POR_DEFECTO =
  "Nada se guardó todavía. Revisa lo propuesto —sobre todo las cifras— y marca "
  + "«Información validada» en lo que esté correcto antes de guardar los cambios.";

/**
 * El botón del encabezado de la pestaña.
 *
 * Se DESHABILITA mientras se guarda la pestaña, no solo mientras lee: mezclar
 * una propuesta dentro de un formulario que está viajando al servidor dejaría
 * filas nuevas fuera del cuerpo que ya salió, y la pantalla las mostraría como
 * guardadas.
 */
export function BotonDeRellenoConIa({
  rellenando,
  disabled = false,
  variante = "clara",
  onRellenar,
}: {
  rellenando: boolean;
  disabled?: boolean;
  /**
   * `oscura` es la del aviso de Construcciones en el diseño: #17181b con texto
   * e ícono blancos, para que no se pierda sobre el verde suave del aviso. Es
   * OPT-IN: las demás pestañas siguen con el botón claro de siempre.
   */
  variante?: "clara" | "oscura";
  onRellenar: () => void;
}) {
  const oscura = variante === "oscura";

  return (
    <Button
      type="button"
      variant={oscura ? "secondary" : "outline"}
      size="sm"
      // Se pisa el `secondary` del DS (que invierte con el tema) por la
      // superficie que NO invierte: el diseño lo pide negro en los dos temas.
      className={
        oscura
          ? "bg-superficie-fija text-superficie-fija-foreground hover:bg-superficie-fija/85"
          : undefined
      }
      disabled={rellenando || disabled}
      onClick={onRellenar}
    >
      {rellenando ? (
        // El Spinner del DS trae su propio `role="status"` y su etiqueta
        // "Loading": acá es DECORATIVO —el texto de al lado dice qué pasa, y en
        // español— así que se lo esconde de la tecnología asistiva. Sin esto
        // hay dos regiones de estado y el lector anuncia "Loading" antes que la
        // frase útil.
        <Spinner role={undefined} aria-label={undefined} aria-hidden="true" />
      ) : (
        <SparklesIcon className={oscura ? "size-4" : "text-primary size-4"} aria-hidden="true" />
      )}
      {rellenando ? "Leyendo el expediente…" : "Rellenar con IA"}
    </Button>
  );
}

/**
 * Una de las listas de documentos del aviso, con su ícono y su rótulo.
 *
 * SON DOS LISTAS CON LA MISMA FORMA y una sola manera de dibujarse: "Documentos
 * leídos" y "No se usaron" viven a once líneas de distancia en el mismo `return`
 * y tenían el markup repetido entero — misma estructura, mismas clases, mismo
 * `key`—, cambiando solo el ícono y el título.
 *
 * NO FUNDE `DocumentoLeidoPorLaIa` CON `DocumentoSinUsar`, que son tipos
 * distintos a propósito (`recortado` dice cuánto entró del documento, y en un
 * "sin usar" no entró nada): pide la forma mínima que las dos cumplen, con
 * `recortado` opcional. Así la lista de los no usados no puede afirmar que se
 * leyó algo entero.
 *
 * El `children` es la nota al pie, que hoy solo lleva una de las dos.
 */
function ListaDeDocumentos({
  titulo,
  icono: Icono,
  documentos,
  children,
}: {
  titulo: string;
  icono: typeof FileTextIcon;
  documentos: readonly { nombre: string; tipo: string; recortado?: boolean }[];
  children?: ReactNode;
}) {
  if (documentos.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      <p className="text-ink-muted text-xs font-bold">{titulo}</p>
      <ul className="space-y-1">
        {documentos.map((documento) => (
          <li
            key={`${documento.tipo}-${documento.nombre}`}
            className="text-ink-body flex items-start gap-2 text-xs"
          >
            <Icono className="text-ink-hint mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              {documento.tipo}
              <span className="text-ink-hint"> · {documento.nombre}</span>
              {/* Recortar en silencio es cómo se llega a una propuesta a la que
                  le falta el capítulo de aguas porque estaba en la página 40. */}
              {documento.recortado ? <span className="text-ink-hint"> (leído en parte)</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {children}
    </div>
  );
}

/**
 * Lo que pasó con el último relleno.
 *
 * `aria-live="polite"`: la propuesta aparece sin que la persona haya movido el
 * foco, y el trabajo puede tardar varios segundos. Sin esto, quien usa un lector
 * de pantalla no se entera ni de que empezó ni de que terminó.
 */
export function AvisoDeLoRellenado({
  rellenando,
  error,
  aviso,
  onDescartar,
  queHacer = QUE_HACER_POR_DEFECTO,
}: {
  rellenando: boolean;
  error: string | null;
  aviso: AvisoDelRelleno | null;
  /** Quita lo que trajo el último relleno. `null` para no ofrecerlo. */
  onDescartar: (() => void) | null;
  /**
   * Qué tiene que hacer la persona ahora.
   *
   * ES UNA PROP CON DEFECTO desde que entró "Maquinarias y equipos"
   * (14-09-2026): en aguas y en suelos la propuesta queda en el BORRADOR y no
   * toca el servidor hasta "Guardar cambios", pero de una máquina cuelgan fotos,
   * así que ahí cada fila se agrega con su propio POST. Decirle a quien mira esa
   * pantalla que "nada se guardó" y que apriete "Guardar cambios" sería falso en
   * las dos mitades.
   */
  queHacer?: string;
}) {
  return (
    <div aria-live="polite">
      {rellenando ? (
        <p className="text-ink-hint text-xs">
          Leyendo los documentos del expediente. Puede tardar unos segundos.
        </p>
      ) : null}

      {error ? <p className="text-destructive text-xs font-medium">{error}</p> : null}

      {aviso ? (
        <div className="bg-info-soft space-y-3 rounded-xl p-4">
          <p className="text-info-soft-foreground text-xs font-bold">{aviso.resumen}</p>

          {/* LA FRASE QUE MÁS IMPORTA DE TODA LA PANTALLA: dice qué quedó
              guardado y qué no. Sin ella, la pantalla se ve exactamente
              igual que después de un guardado real. */}
          <p className="text-ink-body text-sm text-pretty">{queHacer}</p>

          <ListaDeDocumentos
            titulo="Documentos leídos"
            icono={FileTextIcon}
            documentos={aviso.documentos}
          />

          {/* LOS QUE PERDIERON POR PRIORIDAD, que NO son los pendientes.
              Aquellos no se pudieron leer y se resuelven esperando; estos se
              podían leer perfectamente y no se leyeron.

              DESDE EL 21-09-2026 esto ya casi no pasa: sin tope, cada campo lee
              TODOS sus candidatos legibles, y lo único que sigue perdiendo por
              prioridad es una tasación anterior dentro de su grupo (se lee solo
              la más reciente). Sin este bloque, quien sube la tasación de
              Consultek a un informe que ya tiene una externa más reciente ve
              volver la propuesta y no tiene cómo enterarse de que la suya no se
              usó. */}
          <ListaDeDocumentos titulo="No se usaron" icono={FileMinusIcon} documentos={aviso.sinUsar}>
            <p className="text-ink-muted text-xs text-pretty">
              De cada dato se leen todos los documentos que lo puedan traer; ante una
              contradicción, manda el de mayor prioridad. De las tasaciones anteriores se lee
              solo la más reciente, y por eso puede aparecer alguna acá.
            </p>
          </ListaDeDocumentos>

          {aviso.pendientes > 0 ? (
            <p className="text-ink-muted text-xs">
              {aviso.pendientes === 1
                ? "Hay 1 documento más que sirve para esta pestaña y todavía no se puede leer."
                : `Hay ${aviso.pendientes} documentos más que sirven para esta pestaña y todavía no se pueden leer.`}{" "}
              Revisa su estado en Antecedentes legales y vuelve a intentarlo más tarde.
            </p>
          ) : null}

          {onDescartar ? (
            <Button type="button" variant="ghost" size="sm" onClick={onDescartar}>
              Descartar lo propuesto
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
